import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { rangeLabel } from "@/lib/dates";
import { useDebouncedValue } from "@/lib/search";

/**
 * The register's filters, as state — one definition, used by every screen that asks the
 * same question of the same rows.
 *
 * The URL is the only source of truth for what is being looked at. Held in component
 * state, a filtered register could not survive a refresh, could not be reached with the
 * back button, and — the one that matters for this product — could not be sent to
 * anybody. "Which trainings has Ravi attended" is the question this was built to answer,
 * and the answer was previously unshareable.
 *
 * It lives here rather than in Trainings.jsx because Reports asks the same question. Its
 * download buttons used to open `/exports/trainings.pdf` with no query string at all, so
 * the SAME button produced the whole register from one screen and the filtered view from
 * another — with the file itself stating, on its "About this export" sheet, filters that
 * did not match what the person had on screen. Two copies of a filter set drift; this
 * project has already watched the PDF and the Excel exports do exactly that.
 */

export const STATUSES = ["Draft", "Scheduled", "Completed", "Cancelled"];
export const ATTENDANCE = ["Present", "Late", "Absent", "Not Marked"];

// Ours, not the API's: the sort order and which row's panel is open. Both live in the
// URL with the filters, and both are stripped before the string is handed to the server.
const CLIENT_KEYS = ["sort", "open"];

// The filters the register owns, and the query-string key each one travels under. The keys
// are the API's own parameter names, so the URL on screen IS the request — and so the
// export buttons can hand the same string to the server without a second translation.
const SINGLE = { status: "status", type: "training_type", date_from: "date_from", date_to: "date_to",
                 q: "q", sheet: "attendance_sheet" };
const MULTI = { units: "unit_id", scopeDepts: "scope_department_id", categories: "category",
                trainers: "trainer_id", employees: "employee_id", depts: "department_id",
                attendeeUnits: "attendee_unit_id", attendance: "attendance_status" };

// A department belongs to one unit. Narrowing the units drops any department chosen
// under a unit that is no longer in the set — kept, it would silently match nothing,
// and an empty register with no visible reason is the failure this screen exists to
// avoid. `departments.unit_id` is NOT NULL, so this is a rule, not a heuristic.
export const deptsUnder = (depts, unitIds) =>
  unitIds.length === 0 ? depts : depts.filter((d) => unitIds.includes(d.unit_id));

// Each master list: where it comes from, and what a person calls it.
const MASTERS = {
  units: ["/units", "units"], depts: ["/departments", "departments"],
  emps: ["/employees", "employees"], trainers: ["/trainers", "trainers"],
  categories: ["/categories", "categories"],
};

export function useRegisterFilters() {
  const [sp, setSp] = useSearchParams();
  const [units, setUnits] = useState([]);
  const [depts, setDepts] = useState([]);
  const [emps, setEmps] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [categories, setCategories] = useState([]);
  // Which masters have arrived, and which could not be loaded, by their keys in MASTERS.
  const [loaded, setLoaded] = useState([]);
  const [mastersError, setMastersError] = useState([]);

  const f = useMemo(() => ({
    q: sp.get("q") || "",
    status: sp.get("status") || "all",
    type: sp.get("training_type") || "all",
    sheet: sp.get("attendance_sheet") || "all",
    date_from: sp.get("date_from") || "",
    date_to: sp.get("date_to") || "",
    units: sp.getAll("unit_id"),
    scopeDepts: sp.getAll("scope_department_id"),
    categories: sp.getAll("category"),
    trainers: sp.getAll("trainer_id"),
    employees: sp.getAll("employee_id"),
    depts: sp.getAll("department_id"),
    attendeeUnits: sp.getAll("attendee_unit_id"),
    attendance: sp.getAll("attendance_status"),
    sort: sp.get("sort") || "",
    open: sp.get("open") || "",
  }), [sp]);

  const write = useCallback((patch) => {
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      for (const [field, value] of Object.entries(patch)) {
        const key = SINGLE[field] || MULTI[field] || field;
        next.delete(key);
        const values = Array.isArray(value) ? value : [value];
        for (const v of values) {
          // "all" is this screen's word for "no filter", and an empty string is the
          // date pickers'. Neither belongs in a URL somebody is going to paste.
          if (v !== "" && v !== "all" && v != null) next.append(key, v);
        }
      }
      return next;
      // replace, not push: the search box writes here on a debounce, and pushing would
      // bury the previous page under a history entry per pause in typing.
    }, { replace: true });
  }, [setSp]);

  // The search box keeps its own value so typing stays instant, and only the settled
  // value reaches the URL and the server.
  const [qInput, setQInput] = useState(f.q);
  const settledQ = useDebouncedValue(qInput);
  useEffect(() => { if (settledQ !== f.q) write({ q: settledQ }); }, [settledQ]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setQInput(f.q); }, [f.q]); // back/forward, or a pasted link

  // Each master is fetched, and fails, on its own. Behind one Promise.all with no catch, a
  // single refused or timed-out request (/employees, at 1,500 rows, is the likely one) left
  // all five pickers empty, each saying nothing was on record, and every filter in a shared
  // link wore its raw id where its name belonged. A 200 that is not a list (an HTML error
  // page) is a failure too: `.map` on it took the page down.
  //
  // Numbered per list, so only the newest ask for a list speaks for it. StrictMode asks for
  // every list twice on mount, and a picker's Try again can be out beside the toast's; a slow
  // failure landing after a good answer would put "Could not load employees" over a picker
  // already full of people.
  const masterSeq = useRef({});
  const failedRef = useRef([]);
  const loadMasters = useCallback(async (keys) => {
    const set = { units: setUnits, depts: setDepts, emps: setEmps, trainers: setTrainers, categories: setCategories };
    const mine = Object.fromEntries(keys.map((k) => [k, (masterSeq.current[k] = (masterSeq.current[k] || 0) + 1)]));
    const results = await Promise.allSettled(keys.map((k) => api.get(MASTERS[k][0])));
    const current = keys.filter((k) => masterSeq.current[k] === mine[k]);
    if (!current.length) return;
    const ok = [];
    results.forEach((r, i) => {
      const k = keys[i];
      if (current.includes(k) && r.status === "fulfilled" && Array.isArray(r.value.data)) { set[k](r.value.data); ok.push(k); }
    });
    // Merged: an answer speaks only for the lists it asked about.
    const failed = [...failedRef.current.filter((k) => !current.includes(k)), ...current.filter((k) => !ok.includes(k))];
    failedRef.current = failed;
    setLoaded((cur) => [...new Set([...cur, ...ok])]);
    setMastersError(failed);
    // One toast under one id: StrictMode runs the mount effect twice in development, and
    // an answer that arrives later must not leave "Could not load" standing over it.
    if (failed.length) {
      const names = failed.map((k) => MASTERS[k][1]);
      const list = names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0];
      toast.error(`Could not load ${list} for the filters.`, {
        id: "register-masters", duration: 8000,
        action: { label: "Try again", onClick: () => loadMasters(failed) },
      });
    } else {
      toast.dismiss("register-masters");
    }
  }, []);
  useEffect(() => { loadMasters(Object.keys(MASTERS)); }, [loadMasters]);

  // The two unit filters each own a department filter. Changing a unit writes both, so
  // a department under a unit that just left the set leaves with it. Only once the
  // departments have loaded: pruned against an empty list, touching a unit while they were
  // unavailable deleted every department filter from the URL.
  const setUnitsFor = (unitField, deptField) => (ids) => write({
    [unitField]: ids,
    [deptField]: loaded.includes("depts")
      ? f[deptField].filter((did) => deptsUnder(depts, ids).some((d) => d.id === did))
      : f[deptField],
  });

  // Everything except what is ours and means nothing to the API.
  const search = useMemo(() => {
    const p = new URLSearchParams(sp);
    CLIENT_KEYS.forEach((k) => p.delete(k));
    return p.toString();
  }, [sp]);

  const deptOption = (d) => ({ id: d.id, name: d.name, hint: units.find((u) => u.id === d.unit_id)?.name });
  const options = {
    units: units.map((u) => ({ id: u.id, name: u.name, hint: u.location })),
    // Only the departments of the units already chosen. Two plants both have a "Quality",
    // and a list of every department in the company told them apart only by a grey hint.
    scopeDepts: deptsUnder(depts, f.units).map(deptOption),
    attendeeDepts: deptsUnder(depts, f.attendeeUnits).map(deptOption),
    emps: emps.map((e) => ({
      id: e.id, name: e.full_name, hint: `${e.employee_code} · ${e.designation || ""}` })),
    trainers: trainers.map((t) => ({
      id: t.id, name: t.name, hint: [t.type, t.organization].filter(Boolean).join(" · ") })),
    categories: categories.map((c) => ({ id: c, name: c })),
  };

  const masters = { units, depts, emps, trainers, categories };
  // A name whose list could not be loaded says so. The raw id stood in for it, and a
  // register sent as a link read "Attended by: 8d1e2c7a-…" to whoever opened it.
  const nameOf = (master, id, key = "name") =>
    masters[master].find((x) => x.id === id)?.[key] || (mastersError.includes(master) ? "(name not loaded)" : id);
  const pills = [];
  if (f.q) pills.push({ key: "q", label: "Search", value: f.q, onRemove: () => { setQInput(""); write({ q: "" }); } });
  if (f.status !== "all") pills.push({ key: "status", label: "Status", value: f.status, onRemove: () => write({ status: "all" }) });
  if (f.type !== "all") pills.push({ key: "type", label: "Type", value: f.type, onRemove: () => write({ type: "all" }) });
  if (f.sheet !== "all") pills.push({ key: "sheet", label: "Signed sheet", value: f.sheet === "missing" ? "missing" : "on file", onRemove: () => write({ sheet: "all" }) });
  if (f.date_from || f.date_to) {
    pills.push({ key: "dates", label: "Dates", value: rangeLabel({ from: f.date_from, to: f.date_to }),
                 onRemove: () => write({ date_from: "", date_to: "" }) });
  }
  const multiPill = (field, label, master, id, key) => ({
    key: `${field}-${id}`, label, value: nameOf(master, id, key),
    onRemove: () => write({ [field]: f[field].filter((x) => x !== id) }),
  });
  f.units.forEach((id) => pills.push(multiPill("units", "Unit", "units", id)));
  f.scopeDepts.forEach((id) => pills.push(multiPill("scopeDepts", "For dept", "depts", id)));
  f.categories.forEach((c) => pills.push({
    key: `cat-${c}`, label: "Category", value: c,
    onRemove: () => write({ categories: f.categories.filter((x) => x !== c) }) }));
  f.trainers.forEach((id) => pills.push(multiPill("trainers", "Trainer", "trainers", id)));
  f.employees.forEach((id) => pills.push(multiPill("employees", "Attended by", "emps", id, "full_name")));
  f.attendeeUnits.forEach((id) => pills.push(multiPill("attendeeUnits", "From unit", "units", id)));
  f.depts.forEach((id) => pills.push(multiPill("depts", "From dept", "depts", id)));
  f.attendance.forEach((v) => pills.push({
    key: `att-${v}`, label: "Attendance", value: v,
    onRemove: () => write({ attendance: f.attendance.filter((x) => x !== v) }) }));

  const clearAll = () => {
    setQInput("");
    setSp(Object.fromEntries(CLIENT_KEYS.filter((k) => f[k]).map((k) => [k, f[k]])), { replace: true });
  };

  // `mastersError` lists, by their keys in `masters`, the lists that could not be loaded, so
  // a picker can say so rather than "No units on record yet."; `retryMasters` asks again.
  return { f, write, search, pills, clearAll, qInput, setQInput, setUnitsFor, options,
           masters, mastersError, retryMasters: () => loadMasters(mastersError) };
}
